import iro from '@jaames/iro';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import tinyColor from 'tinycolor2';

import { InputRgba } from '../../../forms';
import IroColorPicker from '../../IroColorPicker';
import DefaultColorsPanel from '../DefaultColorPanel';
import GradientPanel from '../GradientPanel';

import { useDebounce } from '../../../../hooks';
import {
  getGradient,
  parseGradient,
  rgbaToArray,
  rgbaToHex
} from '../../../../utils';

import {
  IActiveColor,
  IPropsGradient,
  TPropsChange
} from '../../../../lib/types';

const IroGradient: FC<IPropsGradient> = ({
  value = 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
  onChange = () => ({}),
  format = 'rgb',
  debounceMS = 300,
  debounce = true,
  showAlpha = true,
  showInputs = true,
  showGradientResult = true,
  showGradientStops = true,
  showGradientMode = true,
  showGradientAngle = true,
  showGradientPosition = true,
  allowAddGradientStops = true,
  colorBoardHeight = 120,
  defaultColors,
  showReset = false,
  onReset,
  popupWidth = 280
}) => {
  // Store the initial value for reset functionality
  const initialValue = useRef(value);
  const parsedColors = useCallback(() => {
    try {
      const parsed = parseGradient(value);
      // If parsing failed, return fallback gradient
      if (typeof parsed === 'string') {
        console.warn('Gradient parsing failed, using fallback:', parsed);
        const fallback = parseGradient(
          'linear-gradient(90deg, #ffffff 0%, #000000 100%)'
        );
        // Ensure fallback has valid structure
        if (
          fallback &&
          typeof fallback === 'object' &&
          Array.isArray(fallback.stops) &&
          fallback.stops.length > 0
        ) {
          return fallback;
        }
        // Ultimate fallback with guaranteed structure
        return {
          stops: [
            ['rgba(255, 255, 255, 1)', 0, 0],
            ['rgba(0, 0, 0, 1)', 1, 1]
          ],
          gradient: 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
          modifier: 90,
          type: 'linear'
        };
      }

      // Validate parsed result has required structure
      if (
        parsed &&
        typeof parsed === 'object' &&
        Array.isArray(parsed.stops) &&
        parsed.stops.length > 0
      ) {
        return parsed;
      }

      // If parsed result is invalid, use ultimate fallback
      console.warn('Parsed gradient has invalid structure:', parsed);
      return {
        stops: [
          ['rgba(255, 255, 255, 1)', 0, 0],
          ['rgba(0, 0, 0, 1)', 1, 1]
        ],
        gradient: 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
        modifier: 90,
        type: 'linear'
      };
    } catch (error) {
      console.warn('Error parsing gradient, using ultimate fallback:', error);
      return {
        stops: [
          ['rgba(255, 255, 255, 1)', 0, 0],
          ['rgba(0, 0, 0, 1)', 1, 1]
        ],
        gradient: 'linear-gradient(90deg, #ffffff 0%, #000000 100%)',
        modifier: 90,
        type: 'linear'
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const { stops, type, modifier } = parsedColors();

  const iroPickerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isUpdatingFromGradientStop = useRef<boolean>(false);
  const lastUpdateAttempt = useRef<number>(0); // Track last update attempt
  const isPickerInitialized = useRef<boolean>(false); // Track if picker is already initialized
  const [pickerWidth, setPickerWidth] = useState<number>(200);

  // Safe extraction of stop data with fallbacks
  const safeStops =
    Array.isArray(stops) && stops.length > 0
      ? stops
      : [
          ['rgba(255, 255, 255, 1)', 0, 0],
          ['rgba(0, 0, 0, 1)', 1, 1]
        ];
  const safeLastStop = rgbaToArray(safeStops[safeStops.length - 1][0]);
  const safeParsedLastStop =
    Array.isArray(safeLastStop) && safeLastStop.length >= 4
      ? safeLastStop
      : [255, 255, 255, 1];

  const activeStopIndex = safeStops.length - 1;
  const activeStop = rgbaToHex([
    safeParsedLastStop[0],
    safeParsedLastStop[1],
    safeParsedLastStop[2]
  ]);
  const activeAlpha = Math.round(safeParsedLastStop[3] * 100);
  // Responsive width for IroColorPicker - match solid picker logic
  useEffect(() => {
    if (!containerRef.current) return;
    const resizeObserver = new window.ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        // Use the same logic as solid picker for consistency
        const padding = 40; // Total padding and margins
        const available = containerWidth - padding;

        let calculatedWidth;
        // Simple responsive calculation - same as solid picker
        if (available <= 200) {
          calculatedWidth = Math.max(150, available - 10); // Very small containers
        } else if (available <= 320) {
          calculatedWidth = Math.min(available * 0.85, 250); // Medium containers
        } else {
          calculatedWidth = Math.min(available * 0.8, 280); // Large containers
        }

        setPickerWidth(Math.floor(calculatedWidth));
      }
    });
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const [color, setColor] = useState({
    gradient: value,
    type,
    modifier,
    stops: safeStops
  });

  const [activeColor, setActiveColor] = useState<IActiveColor>({
    hex: activeStop,
    alpha: activeAlpha,
    loc: safeStops[activeStopIndex][1],
    index: activeStopIndex
  });

  const debounceColor = useDebounce(color, debounceMS);

  // Update iro picker color
  const updateIroPickerColor = useCallback(
    (colorData: any, retryCount: number = 0) => {
      const maxRetries = 3; // Reduced from 5 to limit spam

      // Enhanced validation for color data
      if (
        !colorData ||
        typeof colorData.hex !== 'string' ||
        typeof colorData.alpha !== 'number'
      ) {
        return; // Early return if invalid data
      }

      // Throttle rapid calls - but allow calls if color is significantly different
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateAttempt.current;

      // Allow immediate update if throttling time has passed OR if it's a significant color change
      if (timeSinceLastUpdate < 100) {
        return;
      }
      lastUpdateAttempt.current = now;
      const updateColor = () => {
        // Enhanced picker readiness check
        const isPickerReady = () => {
          try {
            return (
              iroPickerRef.current &&
              iroPickerRef.current.colorPicker &&
              iroPickerRef.current.colorPicker.color &&
              typeof iroPickerRef.current.colorPicker.color.set === 'function'
            );
          } catch (error) {
            console.warn('Error checking picker readiness:', error);
            return false;
          }
        };

        // Check if picker is properly initialized
        if (isPickerReady()) {
          const iroColor = showAlpha
            ? {
                r: parseInt(colorData.hex.slice(1, 3), 16),
                g: parseInt(colorData.hex.slice(3, 5), 16),
                b: parseInt(colorData.hex.slice(5, 7), 16),
                a: colorData.alpha / 100
              }
            : colorData.hex;

          try {
            // Set flag to prevent circular updates
            isUpdatingFromGradientStop.current = true;

            iroPickerRef.current.colorPicker.color.set(iroColor);

            // Reset flag after a short delay
            setTimeout(() => {
              isUpdatingFromGradientStop.current = false;
            }, 100);
          } catch (error) {
            isUpdatingFromGradientStop.current = false;

            // Retry with exponential backoff only for actual errors, not readiness issues
            if (retryCount < maxRetries) {
              const delay = 150 + retryCount * 100; // 150ms, 250ms, 350ms
              setTimeout(() => {
                updateIroPickerColor(colorData, retryCount + 1);
              }, delay);
            }
          }
        } else {
          // If picker is not ready, wait longer before retrying
          if (retryCount < 3) {
            // Reduced max retries for readiness checks
            // Longer delays for picker readiness: 200ms, 500ms, 1000ms
            const delay = 200 + retryCount * 300;

            setTimeout(() => {
              updateIroPickerColor(colorData, retryCount + 1);
            }, delay);
          }
        }
      };

      updateColor();
    },
    [showAlpha]
  );

  useEffect(() => {
    if (debounce && debounceColor) {
      onChange?.(debounceColor.gradient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceColor]);

  // Initialize iro picker with current activeColor when component mounts or picker becomes ready
  useEffect(() => {
    const initializeIroPicker = () => {
      // Skip if picker is already initialized and working
      if (
        isPickerInitialized.current &&
        iroPickerRef.current?.colorPicker?.color
      ) {
        return;
      }

      if (
        iroPickerRef.current?.colorPicker?.color &&
        activeColor.hex &&
        typeof activeColor.alpha === 'number'
      ) {
        isPickerInitialized.current = true;

        // Wait a bit for the picker to be fully ready, then update
        setTimeout(() => {
          updateIroPickerColor({
            hex: activeColor.hex,
            alpha: activeColor.alpha
          });
        }, 200);
      }
    };

    // Only initialize if not already initialized
    if (!isPickerInitialized.current) {
      // Try immediately
      initializeIroPicker();

      // Also try after a delay in case picker wasn't ready
      const timeoutId = setTimeout(initializeIroPicker, 500);

      return () => clearTimeout(timeoutId);
    }

    // Return empty cleanup function when no initialization is needed
    return () => {};
  }, [activeColor, updateIroPickerColor]); // Initialize when activeColor or updateFunction changes

  // Custom setActiveColor that also updates iro picker
  const handleSetActiveColor = useCallback(
    (newActiveColor: any) => {
      // Handle both direct values and function updaters
      if (typeof newActiveColor === 'function') {
        // This is a function updater - call setActiveColor properly
        setActiveColor((prev) => {
          const updated = newActiveColor(prev);

          // Note: useEffect will handle iro picker update automatically
          return updated;
        });
      } else {
        // This is a direct value - just update state, useEffect will handle iro picker update
        setActiveColor(newActiveColor);
      }
    },
    [] // No dependencies needed since we're just calling setState
  );

  // Update iro picker when activeColor's hex or alpha changes (not location)
  useEffect(() => {
    const { hex, alpha } = activeColor;

    // Validate activeColor before proceeding
    if (!hex || typeof alpha !== 'number') {
      return;
    }

    // Update iro picker immediately when color changes
    updateIroPickerColor({ hex, alpha });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColor.hex, activeColor.alpha, updateIroPickerColor]); // Only depend on hex and alpha, not full activeColor

  const updateGradient = useCallback(
    (newColor: any) => {
      if (Array.isArray(newColor)) {
        // Handle the case where it's called with stops array
        const newStops = newColor;
        const newColorState = {
          ...color,
          stops: newStops,
          gradient: `${getGradient(
            color.type,
            newStops,
            color.modifier,
            format,
            showAlpha
          )}`
        };
        setColor(newColorState);
        return newColorState; // Return the new state for immediate use
      } else {
        // Handle the case where it's called with full color object
        setColor(newColor);
        return newColor;
      }
    },
    [color, format, showAlpha]
  );

  const onChangeActiveColor = ({ hex, alpha }: TPropsChange) => {
    // Use setActiveColor callback to get the LATEST activeColor state
    setActiveColor((latestActiveColor) => {
      // Use setColor callback to get the most current state
      setColor((currentColorState) => {
        // FIXED: Find the active stop by location using LATEST activeColor
        const activeStopIndex = currentColorState.stops.findIndex(
          (stop) =>
            Math.abs((stop[1] as number) - (latestActiveColor.loc as number)) <
            0.01
        );

        // Safety check: ensure we have a valid index
        if (
          activeStopIndex === -1 ||
          activeStopIndex >= currentColorState.stops.length
        ) {
          console.warn(
            'Could not find stop at location:',
            latestActiveColor.loc,
            'in stops:',
            currentColorState.stops
          );
          return currentColorState; // Return unchanged state
        }

        const currentStop = currentColorState.stops[activeStopIndex];
        const currentPosition = currentStop[1];

        // Update the gradient with the new active color
        const newStops = [...currentColorState.stops];
        const rgba = tinyColor(hex);
        rgba.setAlpha(alpha / 100);

        // Update the correct stop
        newStops[activeStopIndex] = [
          rgba.toRgbString(),
          currentPosition,
          activeStopIndex // Use the found index
        ];

        const newColorState = {
          ...currentColorState,
          stops: newStops,
          gradient: `${getGradient(
            currentColorState.type,
            newStops,
            currentColorState.modifier,
            format,
            showAlpha
          )}`
        };

        return newColorState;
      });

      // Return updated activeColor with new hex and alpha
      const updatedActiveColor = {
        ...latestActiveColor,
        hex,
        alpha
      };

      return updatedActiveColor;
    });
  };

  const setInit = () => {}; // Stub function for gradient panel compatibility

  const onSubmitChange = (rgba: string) => {
    const rgbaArr = rgbaToArray(rgba);
    const hex = rgbaToHex([rgbaArr[0], rgbaArr[1], rgbaArr[2]]);
    const alpha = Math.round(rgbaArr[3] * 100);

    onChangeActiveColor({ hex, alpha });
  };

  // Reset to initial color
  const handleResetColor = () => {
    const initialParsed = parseGradient(initialValue.current);
    setColor(initialParsed);

    // Reset active color to the last stop of the initial gradient
    const lastStop = rgbaToArray(
      initialParsed.stops[initialParsed.stops.length - 1][0]
    );
    const newActiveColor = {
      hex: rgbaToHex([lastStop[0], lastStop[1], lastStop[2]]),
      alpha: Math.round(lastStop[3] * 100),
      loc: initialParsed.stops[initialParsed.stops.length - 1][1],
      index: initialParsed.stops.length - 1
    };
    setActiveColor(newActiveColor);

    // Note: useEffect will handle iro picker update automatically when activeColor changes

    // Call onChange with initial value
    onChange(initialValue.current);

    // Call onReset callback if provided
    onReset?.();
  };

  // Update iro picker when color is selected from default colors panel
  const handleColorFromPanel = (newColor: any) => {
    // Ensure the gradient CSS string is properly set
    if (newColor?.stops && !newColor.gradient) {
      // Reconstruct the gradient string if missing
      newColor.gradient = getGradient(
        newColor.type || 'linear',
        newColor.stops,
        newColor.modifier || 90,
        format,
        showAlpha
      );
    }

    setColor(newColor);

    if (newColor?.stops) {
      const lastStop = rgbaToArray(
        newColor.stops[newColor.stops.length - 1][0]
      );
      const activeStop = rgbaToHex([lastStop[0], lastStop[1], lastStop[2]]);
      const newActiveColor = {
        hex: activeStop,
        alpha: Math.round(lastStop[3] * 100),
        loc: newColor.stops[newColor.stops.length - 1][1],
        index: newColor.stops.length - 1
      };

      setActiveColor(newActiveColor);

      // Note: useEffect will handle iro picker update automatically when activeColor changes
    }
  };

  const handleIroColorChange = (iroColor: any) => {
    // Don't process if we're currently updating from gradient stop
    if (isUpdatingFromGradientStop.current) {
      return;
    }

    const newColor: TPropsChange = {
      hex: iroColor.hexString,
      alpha: Math.round(iroColor.alpha * 100)
    };

    onChangeActiveColor(newColor);
  };

  // Create layout array conditionally based on showAlpha
  const layoutConfig = [
    {
      component: (iro as any).ui.Wheel,
      options: {}
    },
    {
      component: (iro as any).ui.Slider,
      options: {
        sliderType: 'value'
      }
    }
  ];

  if (showAlpha) {
    layoutConfig.push({
      component: (iro as any).ui.Slider,
      options: {
        sliderType: 'alpha'
      }
    });
  }

  // Use hex8 format when alpha is enabled for proper alpha support
  const iroColorValue = showAlpha
    ? tinyColor({
        r: parseInt(activeColor.hex.slice(1, 3), 16),
        g: parseInt(activeColor.hex.slice(3, 5), 16),
        b: parseInt(activeColor.hex.slice(5, 7), 16),
        a: activeColor.alpha / 100
      }).toHex8String()
    : activeColor.hex;

  return (
    <div
      className='w-full rounded-xl shadow-lg space-y-2 transition-all duration-200 hover:shadow-xl'
      style={{
        backgroundColor: 'var(--colorpicker-panel-bg)'
      }}
    >
      {/* Color Picker Container */}
      <div className='relative' ref={containerRef}>
        <div
          className='flex justify-center items-center rounded-lg'
          style={{
            height: colorBoardHeight + pickerWidth
          }}
        >
          <IroColorPicker
            ref={iroPickerRef}
            width={pickerWidth}
            color={iroColorValue}
            layout={layoutConfig}
            onColorChange={handleIroColorChange}
          />
        </div>
      </div>

      {/* Gradient Controls */}
      <div className='rounded-lg colorpicker-glass px-4'>
        <GradientPanel
          color={color}
          activeColor={activeColor}
          setActiveColor={handleSetActiveColor}
          setColor={updateGradient}
          setInit={setInit}
          format={format}
          showAlpha={showAlpha}
          showGradientResult={showGradientResult}
          showGradientStops={showGradientStops}
          showGradientMode={showGradientMode}
          showGradientAngle={showGradientAngle}
          showGradientPosition={showGradientPosition}
          allowAddGradientStops={allowAddGradientStops}
        />
      </div>

      {/* Color Palette */}
      <div className='pt-4 px-2'>
        <DefaultColorsPanel
          defaultColors={defaultColors}
          setColor={handleColorFromPanel}
          setInit={() => {}}
          setActiveColor={handleSetActiveColor}
          colorType='gradient'
          currentValue={color.gradient}
          size={
            popupWidth < 250
              ? 'compact'
              : popupWidth <= 350
              ? 'default'
              : 'large'
          }
        />
      </div>

      {/* Input Controls */}
      {showInputs && (
        <div className='rounded-lg colorpicker-glass flex items-center gap-2'>
          <div className='flex-1'>
            <InputRgba
              hex={activeColor.hex}
              alpha={activeColor.alpha}
              showAlpha={showAlpha}
              onChange={(value: { hex: string; alpha: number }) =>
                setActiveColor((prev) => ({
                  ...prev,
                  hex: value.hex,
                  alpha: value.alpha,
                  index: prev.index // Preserve the index!
                }))
              }
              onSubmitChange={onSubmitChange}
            />
          </div>
          {showReset && (
            <button
              onClick={handleResetColor}
              className='px-3 py-2 text-xs font-medium rounded-md transition-colors duration-200 bg-transparent border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 hover:bg-gray-800/50 whitespace-nowrap flex items-center gap-1'
              title='Reset to initial color'
            >
              <svg
                className='w-3 h-3'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                />
              </svg>
              Reset
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default IroGradient;
